import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { useApiStore, useUserStore } from "@finitless/shared/stores";
import { toast } from "sonner";

/**
 * Hook to get global configurations in dictionary mode
 * The query is keyed by the API project, so a project change (e.g. auth applying
 * `?project=` over a saved session) refetches instead of reusing another
 * project's configs. `project` is the project the data was fetched for.
 * @returns {Object} Query result with global configurations data
 */
export const useGlobalConfigs = () => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const project = useApiStore((state) => state.project);

  const query = useQuery({
    queryKey: ["global-configs", "first-load", project],
    queryFn: async () => {
      // The API client builds the URL from the store's project at request time.
      const requestedProject = useApiStore.getState().project;
      const response = await apiClient.get("/frontends/first_load");
      const result = response?.result || {};
      return {
        configs: result.configs || {},
        features: result.features || {},
        project: requestedProject || null,
      };
    },
    enabled: isAuthenticated && isEndpointEnabled("configs", "list"),
    staleTime: 15 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
  });

  return {
    ...query,
    data: query.data?.configs || {},
    features: query.data?.features || {},
    project: query.data?.project ?? null,
  };
};

/**
 * Hook to get configurations with custom query parameters
 * @param {Object} params - Query parameters including where clause
 * @param {Object} options - Additional query options
 * @returns {Object} Query result with configurations data
 */
export const useConfigs = (params = {}, options = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["configs", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/configs${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || [];
    },
    enabled: options.enabled !== false && isEndpointEnabled("configs", "list"),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: 2,
  });
};

/**
 * Hook to update a configuration value
 * @returns {Object} Mutation object
 */
export const useUpdateConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ configId, value }) => {
      const response = await apiClient.put(`/configs/${configId}`, { value });
      return response;
    },
    onSuccess: () => {
      // Invalidate all config queries
      queryClient.invalidateQueries({ queryKey: ["configs"] });
      queryClient.invalidateQueries({ queryKey: ["global-configs"] });

      toast.success("Configuration updated successfully");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update configuration");
    },
  });
};

export default useGlobalConfigs;
